import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  Plus,
  Moon,
  Sunrise,
  Send,
  Pencil,
  Trash2,
  Heart,
  X,
  Check,
  ClipboardCopy,
} from "lucide-react";
import { toast } from "sonner";
import PageContainer from "@/components/PageContainer";
import PageHeader from "@/components/PageHeader";
import SoftButton from "@/components/SoftButton";
import {
  getDrafts,
  addDraft,
  updateDraft,
  deleteDraft,
  holdAgain,
  markSent,
  releaseDraft,
  getResurfacedDrafts,
} from "@/lib/storage";
import { DRAFTS } from "@/constants/testIds";
import AutoSaveIndicator from "@/components/AutoSaveIndicator";

const formatDate = (iso) =>
  new Date(iso).toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

const formatMorning = (iso) =>
  new Date(iso).toLocaleString(undefined, {
    weekday: "short",
    hour: "numeric",
    minute: "2-digit",
  });

export default function Drafts() {
  const [params] = useSearchParams();
  const startCompose = params.get("compose") === "1";

  const [drafts, setDrafts] = useState([]);
  const [composing, setComposing] = useState(startCompose);
  const [editingId, setEditingId] = useState(null);
  const [text, setText] = useState("");
  const [recipient, setRecipient] = useState("");
  const textareaRef = useRef(null);

  useEffect(() => {
    setDrafts(getDrafts());
  }, []);

  useEffect(() => {
    if (composing) {
      setTimeout(() => textareaRef.current?.focus(), 80);
    }
  }, [composing]);

  const refresh = () => setDrafts(getDrafts());

  // drafts is kept as a dependency to re-run this memo when the drafts state changes;
  // getResurfacedDrafts reads from localStorage, so the dep is intentional as a trigger.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const resurfaced = useMemo(() => getResurfacedDrafts(), [drafts]);
  const stillHeld = useMemo(
    () =>
      drafts.filter(
        (d) =>
          d.status === "held" &&
          new Date(d.surfaceAfter).getTime() > Date.now()
      ),
    [drafts]
  );
  const decided = useMemo(
    () => drafts.filter((d) => d.status !== "held"),
    [drafts]
  );

  const openCompose = () => {
    setEditingId(null);
    setText("");
    setRecipient("");
    setComposing(true);
  };

  const openRevise = (d) => {
    setEditingId(d.id);
    setText(d.text);
    setRecipient(d.recipient || "");
    setComposing(true);
  };

  const handleHold = () => {
    if (!text.trim()) return;
    if (editingId) {
      updateDraft(editingId, {
        text: text.trim(),
        recipient: recipient.trim() || null,
      });
      toast.success("Updated. Held safely until morning.");
    } else {
      addDraft({ text, recipient });
      toast.success("Held until morning.", {
        description: "You do not have to decide tonight.",
      });
    }
    setComposing(false);
    setEditingId(null);
    setText("");
    setRecipient("");
    refresh();
  };

  const handleCancel = () => {
    setComposing(false);
    setEditingId(null);
    setText("");
    setRecipient("");
  };

  const handleMarkSent = async (d) => {
    if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(d.text);
        toast.success("Copied. You chose to send.", {
          description: "Paste it wherever you need.",
        });
      } catch {
        toast("Marked as sent.", {
          description: "Copy didn't work automatically — your text is still safe here.",
        });
      }
    } else {
      toast("Marked as sent.");
    }
    markSent(d.id);
    refresh();
  };

  const handleRelease = (id) => {
    releaseDraft(id);
    refresh();
    toast("Released. You did not have to send it.");
  };

  const handleHoldAgain = (id) => {
    holdAgain(id);
    refresh();
    toast("Held again until next morning.", {
      description: "You don't have to decide yet.",
    });
  };

  const handleDelete = (id) => {
    deleteDraft(id);
    refresh();
  };

  return (
    <PageContainer testid={DRAFTS.root}>
      <PageHeader
        title="Drafts on hold"
        subtitle="You do not have to decide tonight. Sleep on it safely."
        variant="exit"
        exitTo="/home"
        exitLabel="Exit"
        testid={DRAFTS.exit}
      />

      {/* Resurfaced banner */}
      {resurfaced.length > 0 && (
        <motion.div
          data-testid={DRAFTS.resurfacedBanner}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-3xl p-5 mb-6 relative overflow-hidden"
          style={{
            background:
              "linear-gradient(135deg, #FBF6EF 0%, #F0E4D8 60%, #EBE5D9 100%)",
            boxShadow: "0 8px 26px rgba(74,32,64,0.08)",
          }}
        >
          <div className="flex items-start gap-3">
            <Sunrise
              size={18}
              className="mt-0.5 shrink-0"
              style={{ color: "var(--un-terracotta)" }}
            />
            <div>
              <p
                className="text-[14px] font-medium"
                style={{
                  color: "var(--un-text-1)",
                  fontFamily: "Manrope, sans-serif",
                }}
              >
                {resurfaced.length} message{resurfaced.length > 1 ? "s" : ""}{" "}
                you held last night.
              </p>
              <p
                className="text-[14px] italic mt-1 leading-snug"
                style={{
                  color: "var(--un-text-2)",
                  fontFamily: "Cormorant Garamond, serif",
                  fontSize: "16px",
                }}
              >
                Still want to send? Does it still feel true?
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* New / Compose */}
      {!composing && (
        <SoftButton
          data-testid={DRAFTS.newBtn}
          variant="primary"
          size="lg"
          iconLeft={<Plus size={18} />}
          onClick={openCompose}
          className="w-full mb-6"
        >
          Hold a message until morning
        </SoftButton>
      )}

      <AnimatePresence>
        {composing && (
          <motion.div
            initial={{ opacity: 0, height: 0, y: 8 }}
            animate={{ opacity: 1, height: "auto", y: 0 }}
            exit={{ opacity: 0, height: 0, y: 8 }}
            transition={{ duration: 0.3 }}
            className="un-card p-5 mb-7 overflow-hidden"
          >
            <p
              className="text-[10px] uppercase tracking-[0.28em] mb-2"
              style={{
                color: "var(--un-text-3)",
                fontFamily: "Manrope, sans-serif",
              }}
            >
              {editingId ? "Revising" : "Compose"}
            </p>
            <textarea
              ref={textareaRef}
              data-testid={DRAFTS.textarea}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Write it. You don't have to send it tonight."
              rows={6}
              className="w-full resize-none outline-none rounded-2xl p-4 text-[15px] leading-relaxed mb-2"
              style={{
                background: "var(--un-bg-2)",
                color: "var(--un-text-1)",
                fontFamily: "DM Sans, sans-serif",
              }}
            />
            <input
              data-testid={DRAFTS.recipient}
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              placeholder="To (optional)"
              className="w-full outline-none rounded-2xl p-3 text-sm"
              style={{
                background: "var(--un-bg-2)",
                color: "var(--un-text-1)",
                fontFamily: "DM Sans, sans-serif",
              }}
            />
            <div className="mt-2 flex justify-end">
              <AutoSaveIndicator watch={text} label="Held safely on your device" />
            </div>
            <div className="flex items-center justify-end gap-2 mt-4">
              <SoftButton
                data-testid={DRAFTS.cancel}
                variant="ghost"
                size="sm"
                onClick={handleCancel}
                iconLeft={<X size={14} />}
              >
                Cancel
              </SoftButton>
              <SoftButton
                data-testid={DRAFTS.hold}
                variant="primary"
                size="sm"
                onClick={handleHold}
                disabled={!text.trim()}
                iconLeft={<Moon size={14} />}
              >
                {editingId ? "Save changes" : "Hold until morning"}
              </SoftButton>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Resurfaced (ready to decide) */}
      {resurfaced.length > 0 && (
        <Group title="Ready to revisit" accent="#8E6C88">
          {resurfaced.map((d) => (
            <DraftCard
              key={d.id}
              d={d}
              primary={
                <SoftButton
                  data-testid={`${DRAFTS.sentBtn}${d.id}`}
                  variant="primary"
                  size="sm"
                  iconLeft={<Send size={12} />}
                  onClick={() => handleMarkSent(d)}
                >
                  Send
                </SoftButton>
              }
              links={[
                {
                  label: "Revise",
                  testid: `${DRAFTS.reviseBtn}${d.id}`,
                  onClick: () => openRevise(d),
                  icon: Pencil,
                },
                {
                  label: "Hold again",
                  testid: `${DRAFTS.holdAgainBtn}${d.id}`,
                  onClick: () => handleHoldAgain(d.id),
                  icon: Moon,
                },
                {
                  label: "Release",
                  testid: `${DRAFTS.releaseBtn}${d.id}`,
                  onClick: () => handleRelease(d.id),
                  icon: Heart,
                },
              ]}
            />
          ))}
        </Group>
      )}

      {/* Still held (waiting for morning) */}
      {stillHeld.length > 0 && (
        <Group title="Held until morning" accent="#5B3954">
          {stillHeld.map((d) => (
            <DraftCard
              key={d.id}
              d={d}
              metaRight={
                <span
                  className="text-[10px] tracking-wide flex items-center gap-1"
                  style={{
                    color: "var(--un-text-3)",
                    fontFamily: "Manrope, sans-serif",
                  }}
                >
                  <Moon size={10} strokeWidth={1.6} />
                  surfaces {formatMorning(d.surfaceAfter)}
                </span>
              }
              links={[
                {
                  label: "Revise",
                  testid: `${DRAFTS.reviseBtn}${d.id}`,
                  onClick: () => openRevise(d),
                  icon: Pencil,
                },
                {
                  label: "Release",
                  testid: `${DRAFTS.releaseBtn}${d.id}`,
                  onClick: () => handleRelease(d.id),
                  icon: Heart,
                },
              ]}
            />
          ))}
        </Group>
      )}

      {/* Decided / archived */}
      {decided.length > 0 && (
        <Group title="Decided" accent="#8A7985">
          {decided.map((d) => (
            <DraftCard
              key={d.id}
              d={d}
              muted
              metaRight={
                <span
                  className="text-[10px] font-medium tracking-wide uppercase"
                  style={{
                    color: d.status === "sent" ? "var(--un-safe)" : "var(--un-text-3)",
                    fontFamily: "Manrope, sans-serif",
                  }}
                >
                  {d.status === "sent" ? "sent" : "released"}
                </span>
              }
              links={[
                {
                  label: "Delete",
                  testid: `${DRAFTS.deleteBtn}${d.id}`,
                  onClick: () => handleDelete(d.id),
                  icon: Trash2,
                },
              ]}
            />
          ))}
        </Group>
      )}

      {/* Empty state */}
      {drafts.length === 0 && !composing && (
        <div
          data-testid={DRAFTS.emptyState}
          className="text-center py-10 px-6"
        >
          <div
            className="mx-auto w-16 h-16 rounded-full mb-4 flex items-center justify-center"
            style={{ background: "rgba(74,32,64,0.06)" }}
          >
            <Moon size={20} style={{ color: "var(--un-plum)" }} />
          </div>
          <p
            className="italic text-[17px] leading-relaxed max-w-[320px] mx-auto"
            style={{
              color: "var(--un-text-3)",
              fontFamily: "Cormorant Garamond, serif",
            }}
          >
            Sleep on it safely. Tomorrow-you decides — not 11pm-you.
          </p>
        </div>
      )}
    </PageContainer>
  );
}

function Group({ title, accent, children }) {
  return (
    <section className="mb-7">
      <div className="flex items-center gap-2 mb-3 px-1">
        <span
          className="w-1.5 h-1.5 rounded-full"
          style={{ background: accent }}
        />
        <p
          className="text-[10px] uppercase tracking-[0.28em] font-medium"
          style={{ color: accent, fontFamily: "Manrope, sans-serif" }}
        >
          {title}
        </p>
      </div>
      <div className="flex flex-col gap-3">{children}</div>
    </section>
  );
}

function DraftCard({ d, primary, links, metaRight, muted }) {
  return (
    <motion.article
      data-testid={`${DRAFTS.itemPrefix}${d.id}`}
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className="un-card p-5"
      style={muted ? { opacity: 0.78 } : undefined}
    >
      <div className="flex items-center justify-between gap-3 mb-2">
        <p
          className="text-[10px] tracking-[0.22em] uppercase"
          style={{
            color: "var(--un-text-3)",
            fontFamily: "Manrope, sans-serif",
          }}
        >
          {formatDate(d.createdAt)}
          {d.recipient ? ` · to ${d.recipient}` : ""}
        </p>
        {metaRight}
      </div>
      <p
        className="text-[15px] leading-relaxed whitespace-pre-wrap mb-4"
        style={{ color: "var(--un-text-1)" }}
      >
        {d.text}
      </p>
      {primary && <div className="mb-3">{primary}</div>}
      {links && links.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          {links.map(({ label, testid, onClick, icon: Icon }) => (
            <button
              key={label}
              data-testid={testid}
              onClick={onClick}
              className="inline-flex items-center gap-1.5 text-[12px] un-press"
              style={{
                color: "var(--un-text-3)",
                fontFamily: "Manrope, sans-serif",
              }}
            >
              {Icon && <Icon size={11} strokeWidth={1.6} />}
              {label}
            </button>
          ))}
        </div>
      )}
    </motion.article>
  );
}
